import {
  Injectable,
  NgZone
} from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject
} from 'rxjs';

import { ApiService }               from '../api.service';
import { CableConnectOptionsModel } from './typings/websocket.cable-connect-options';
import { CableFrameModel }          from './typings/websocket.cable-frame';
import { CableSubscriptionModel }   from './typings/websocket.cable-subscription';

export type CableFrameType =
  | 'welcome'
  | 'ping'
  | 'confirm_subscription'
  | 'reject_subscription'
  | 'disconnect';

@Injectable({ providedIn: 'root' })
export class WebSocketService {
  private baseUrl       : string = this.getBaseUrl();
  private connectOpts  ?: CableConnectOptionsModel;
  private ws           ?: WebSocket;
  private pendingFrames : any[] = [];

  private connected$ = new BehaviorSubject<boolean>(false);
  private frames$    = new Subject<CableFrameModel>();
  private isOpen     = false;
  private messages$  = new Subject<{ identifier: string; message: any }>();

  private channels          = new Set<string>();
  private lastPingAt        = 0;
  private pingWatchdog      : any = null;
  private reconnectAttempts = 0;
  private reconnectTimer    : any = null;
  private rejectedSessionId : string | null = null;
  private shouldReconnect   = false;

  constructor (
    private apiService: ApiService,
    private zone      : NgZone
  ) {}

  private buildCableUrl ( path : string, paramName : string, sessionId? : string) : string {
    const trimmedBase = this.baseUrl.replace(/\/+$/, '');
    const trimmedPath = path.startsWith('/') ? path : `/${path}`;
    const url         = `${trimmedBase}${trimmedPath}`;

    if (!sessionId) {
      return url;
    }

    const joiner = url.includes('?') ? '&' : '?';
    return `${url}${joiner}${encodeURIComponent(paramName)}=${encodeURIComponent(sessionId)}`;
  }

  connection$ () : Observable<boolean> {
    return this.connected$.asObservable();
  }

  connect ( opts : CableConnectOptionsModel = {} ) : void {
    // disconnect() resets the backoff count
    const reconnectAttempts = this.reconnectAttempts;

    this.disconnect();

    this.reconnectAttempts = reconnectAttempts;

    // Keep the caller's raw options so reconnects re-read the session cookie fresh
    this.connectOpts     = { ...opts };
    this.shouldReconnect = true;

    const cablePath        = opts.cablePath || '/cable';
    const sessionParamName = opts.sessionParamName || 'session_id';
    const sessionId        = opts.sessionId || this.readCookie('session_id');

    const url              = this.buildCableUrl(cablePath, sessionParamName, sessionId);

    this.ws                = new WebSocket(url);

    this.ws.onopen = () => {
      // ActionCable will still send "welcome" next.
      this.zone.run(() => {
        this.connected$.next(true);
      });
    };

    this.ws.onmessage = ( event : MessageEvent ) => {
      let frame: CableFrameModel;
      try {
        frame = JSON.parse(event.data);
      } catch {
        return;
      }

      // Pings are the liveness signal for the stale-connection watchdog, but stay unforwarded
      if (frame.type === 'ping'){
        this.lastPingAt = Date.now();
        return;
      }

      this.zone.run(() => {
        this.frames$.next(frame);

        if (frame.type === 'disconnect' && frame.reconnect === false) {
          this.disconnect();

          this.rejectedSessionId = this.connectOpts?.sessionId || this.readCookie('session_id') || null;
          window.addEventListener('focus', this.onFocus);
          return;
        }

        if (frame.type === 'welcome') {
          this.isOpen            = true;
          this.lastPingAt        = Date.now();
          this.reconnectAttempts = 0;
          this.resubscribe();
          this.flushPending();
          return;
        }

        // Real broadcast payload from stream
        if (frame.message && frame.identifier) {
          this.messages$.next({ identifier: frame.identifier, message: frame.message });
        }
      });
    };

    this.ws.onerror = (err) => {
    };

    this.ws.onclose = () => {
      this.isOpen = false;
      this.zone.run(() => this.connected$.next(false));
      this.scheduleReconnect();
    };

    window.addEventListener('online', this.onOnline);
    this.startPingWatchdog();
  }

  disconnect () : void {
    this.rejectedSessionId = null;
    this.shouldReconnect   = false;

    window.removeEventListener('focus', this.onFocus);
    window.removeEventListener('online', this.onOnline);
    this.stopPingWatchdog();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;

    this.pendingFrames = [];
    this.isOpen        = false;

    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      // A deliberately closed socket must never schedule a reconnect from its async onclose
      this.ws.onclose = null;
      this.ws.close(1000, 'client disconnect');
    }
    this.ws = undefined;
    this.connected$.next(false);
  }

  /** (welcome, confirm_subscription, disconnect) */
  events$ () : Observable<CableFrameModel> {
    return this.frames$.asObservable();
  }

  private flushPending () : void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    const frames       = [...this.pendingFrames];
    this.pendingFrames = [];

    for (const f of frames) {
      this.ws.send(JSON.stringify(f));
    }
  }

  getBaseUrl () : string {
    const endpoint = this.apiService.getEndpoint();
    const baseUrl  = new URL(endpoint);

    baseUrl.protocol = 'wss:';

    return baseUrl.toString();
  }

  /** Only real broadcast payloads */
  notifications$ () : Observable<{ identifier : string; message : any }> {
    return this.messages$.asObservable();
  }

  // A rejected tab retries only once the user has signed in again elsewhere
  private onFocus = () => {
    const sessionId = this.readCookie('session_id');

    if (sessionId && sessionId !== this.rejectedSessionId) {
      this.connect(this.connectOpts);
    }
  };

  private onOnline = () => {
    if (this.shouldReconnect && !this.isOpen) {
      this.reconnectNow();
    }
  };

  private readCookie ( name : string ) : string | undefined {
    // simple cookie read
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : undefined;
  }

  private reconnectNow () : void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
    this.connect(this.connectOpts);
  }

  private resubscribe () : void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    for (const identifier of this.channels) {
      this.ws.send(JSON.stringify({ command: 'subscribe', identifier }));
    }
  }

  // Retry unexpected drops with a capped backoff (1s doubling to 30s); a deliberate disconnect()
  // clears shouldReconnect so it never fires for those
  private scheduleReconnect () : void {
    if (!this.shouldReconnect || this.reconnectTimer) {
      return;
    }

    const delay = Math.min(30000, 1000 * Math.pow(2, this.reconnectAttempts));

    this.reconnectAttempts++;

    this.zone.runOutsideAngular(() => {
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.connect(this.connectOpts);
      }, delay);
    });
  }

  // ActionCable pings every ~3s; a longer silence means the socket died without firing onclose
  // (network drop, laptop sleep), so force-close it and let the reconnect path take over
  private startPingWatchdog () : void {
    this.stopPingWatchdog();

    this.zone.runOutsideAngular(() => {
      this.pingWatchdog = setInterval(() => {
        if (this.isOpen && Date.now() - this.lastPingAt > 15000 && this.ws) {
          this.ws.close();
        }
      }, 5000);
    });
  }

  private stopPingWatchdog () : void {
    if (this.pingWatchdog) {
      clearInterval(this.pingWatchdog);
      this.pingWatchdog = null;
    }
  }

  subscribe ( channel : string, params : Record<string, any> = {}): CableSubscriptionModel {
    const identifier = JSON.stringify({ channel, ...params });

    // Registered channels are (re)subscribed on every welcome, so only send when already open
    this.channels.add(identifier);

    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.isOpen) {
      this.ws.send(JSON.stringify({ command: 'subscribe', identifier }));
    }

    return {
      identifier,
      unsubscribe: () => {
        this.channels.delete(identifier);
        this.sendOrQueue({ command: 'unsubscribe', identifier });
      },
      perform: ( action : string, payload : Record<string, any> = {}) => {
        const data = { action, ...payload };

        // ActionCable "perform"
        this.sendOrQueue({
          command: 'message',
          identifier,
          data   : JSON.stringify({ action, ...payload })
        });
      }
    };
  }

  private sendOrQueue ( frame : any ) : void {

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.isOpen) {
      this.pendingFrames.push(frame);
      return;
    }

    this.ws.send(JSON.stringify(frame));
  }

}
